import { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchPatientPrescriptions,              // 👤 patient self
  fetchPrescriptionsByPatientForDoctor,   // 👨‍⚕️ doctor view
} from "../redux/thunks/prescriptionThunks";

import {
  selectPatientPrescriptions,
  selectPatientPrescriptionsStatus,
  selectPatientPrescriptionsError,

  selectDoctorPatientPrescriptions,
  selectDoctorPatientPrescriptionsStatus,
  selectDoctorPatientPrescriptionsError,
} from "../redux/slices/prescriptionSlice";

import useDoctors from "../../authDoctor/hooks/useDoctors";

const DOCUMENT_TYPE_LABELS = {
  prescription: "Ordonnance",
  bilan: "Bilan / Test",
  radio: "Radiographie / IRM",
  lettre: "Lettre",
};

export const usePrescriptions = ({ patientId = null, doctorId = null }) => {
  const dispatch = useDispatch();
  const { doctors, loading: doctorsLoading } = useDoctors();

  const isDoctorView = Boolean(doctorId && patientId);

  // 🟢 Redux sources
  const patientSelfPrescriptions = useSelector(selectPatientPrescriptions);
  const patientSelfStatus = useSelector(selectPatientPrescriptionsStatus);
  const patientSelfError = useSelector(selectPatientPrescriptionsError);

  const doctorPatientPrescriptions = useSelector(selectDoctorPatientPrescriptions);
  const doctorPatientStatus = useSelector(selectDoctorPatientPrescriptionsStatus);
  const doctorPatientError = useSelector(selectDoctorPatientPrescriptionsError);

  // 🟢 Active data
  const prescriptions = isDoctorView
    ? doctorPatientPrescriptions
    : patientSelfPrescriptions;

  const status = isDoctorView ? doctorPatientStatus : patientSelfStatus;
  const error = isDoctorView ? doctorPatientError : patientSelfError;

  // 🟢 Normalize API shapes
  const normalizedPrescriptions = useMemo(() => {
    if (!prescriptions) return [];
    if (Array.isArray(prescriptions)) return prescriptions;
    if (Array.isArray(prescriptions?.data)) return prescriptions.data;
    if (Array.isArray(prescriptions?.prescriptions)) return prescriptions.prescriptions;
    return [];
  }, [prescriptions]);

  // 🟢 Map doctors
  const doctorsMap = useMemo(() => {
    const map = new Map();
    doctors?.forEach((doc) => {
      const key = String(doc.id ?? doc.doctorId ?? doc.pgId ?? doc._id ?? "").trim();
      if (key) map.set(key, doc.fullName || doc.name || "Unknown Doctor");
    });
    return map;
  }, [doctors]);

  // 🟢 UI-ready items
  const prescriptionItems = useMemo(() => {
    return normalizedPrescriptions.map((p) => {
      const pid = p?.id ?? p?._id ?? p?.prescriptionId;

      const doctorKey = String(
        p?.medecinId ??
        p?.doctorId ??
        p?.doctor?.id ??
        ""
      ).trim();

      const doctorName =
        doctorsMap.get(doctorKey) ||
        p?.medecinName ||
        p?.doctorName ||
        p?.doctor?.fullName ||
        "Unknown Doctor";

      const dateSrc = p?.updatedAt || p?.createdAt;
      const dateLabel = dateSrc
        ? new Date(dateSrc).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "N/A";

      return {
        id: String(pid),
        name: p?.description || p?.content || "Prescription",
        type:
          DOCUMENT_TYPE_LABELS[p?.documentType] ||
          DOCUMENT_TYPE_LABELS[p?.type] ||
          p?.typeOrdonnance ||
          p?.type ||
          "Medical",
        doctorName,
        dateLabel,
        _original: p,
      };
    });
  }, [normalizedPrescriptions, doctorsMap]);

  // ============================================================
  // 🔥 FETCH (KEEP BOTH THUNKS)
  // ============================================================
  useEffect(() => {
    if (!patientId) return;

    if (isDoctorView) {
      // 👨‍⚕️ doctor side — DO NOT BREAK
      dispatch(fetchPrescriptionsByPatientForDoctor({ doctorId, patientId }));
    } else {
      // 👤 patient self
      dispatch(fetchPatientPrescriptions({ patientId }));
    }
  }, [dispatch, patientId, doctorId, isDoctorView]);

  // 🔁 refetch
  const refetch = useCallback(() => {
    if (!patientId) return;

    if (isDoctorView) {
      dispatch(fetchPrescriptionsByPatientForDoctor({ doctorId, patientId }));
    } else {
      dispatch(fetchPatientPrescriptions({ patientId }));
    }
  }, [dispatch, patientId, doctorId, isDoctorView]);

  const loading = status === "loading" || doctorsLoading;

  return {
    prescriptionItems,
    rawPrescriptions: normalizedPrescriptions,
    loading,
    error,
    status,
    refetch,
  };
};

export default usePrescriptions;
